import { useTheme } from "@emotion/react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useAssetSentinelData } from "../AssetSentinel/useAssetSentinelData";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import { Box, Table, TableBody, TableCell, TableRow } from "@mui/material";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../components/Buttons/PATEditButton";
import { AMSModules, paginationStyle } from "../../utils/constants";
import { StatusBadge } from "../../utils/functions";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { getRowsPerPageOptions } from "../../utils/functions";
import { makeStyles } from "@mui/styles";
import FundingSourceTableToolbar from "./FundingSourceTableToolbar";
import AddEditFundingSourceDialog from "./AddEditFundingSourceDialog";
import FundingDetailDialog from "./FundingDetailDialog";
// import FundingSourceTableToolbar from "./FundingSourceTableToolbar";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

export const initialFilterState = {
	make: [],
	model: [],
	year: [],
	department: [],
	employee: [],
	funding: [],
};

const useStyles = makeStyles((theme) => ({
	title: {
		color: theme.palette.text.primary,
		fontWeight: 600,
	},
	colorTitle: {
		color: theme.palette.mode === "dark" ? "#fff" : "#3b3e66",
	},
	cardBackground: {
		background:
			theme.palette.mode === "dark" ? "#1e2e3a" : "rgb(242, 242, 242)",
	},
}));

const FundingSources = () => {
	const classes = useStyles();

	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();
	const {
		fundingSources,
		dispensableItems,
		vehicles,
		workOrders,
		equipment,
		maintenance,
	} = useAssetSentinelData();
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);

	const total = fundingSources?.length || 0;
	const loading = false;

	const [search, setSearch] = useState("");
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo?.inspections || initialFilterState,
	);
	const [openDialog, setOpenDialog] = useState(false);
	const [openDetailDialog, setOpenDetailDialog] = useState(false);
	const [selectedFundingSource, setSelectedFundingSource] = useState(null);

	const currentAgency = useRef(null);

	const calcRemaining = (f) => {
		const spentDisp = (dispensableItems || []).reduce((s, i) => {
			if (i.fundingSourceID !== f.id) return s;
			return (
				s +
				(i.issuances || [])
					.filter((iss) => iss.isRestock && iss.purchaseCost != null)
					.reduce((a, iss) => a + (iss.purchaseCost || 0), 0)
			);
		}, 0);
		const spent =
			(vehicles || [])
				.filter((v) => v.fundingSourceID === f.id)
				.reduce((s, v) => s + (v.originalCost || 0), 0) +
			(equipment || [])
				.filter((e) => e.fundingSourceID === f.id)
				.reduce((s, e) => s + (e.cost || 0), 0) +
			(workOrders || [])
				.filter((w) => w.fundingSourceID === f.id)
				.reduce((s, w) => s + (w.cost || 0), 0) +
			(maintenance || [])
				.filter((m) => m.fundingSourceID === f.id)
				.reduce((s, m) => s + (m.cost || 0), 0) +
			spentDisp;
		return (f.amount || 0) - spent;
	};

	const baseColumns = useMemo(
		() => [
			{
				field: "title",
				headerName: "Title",
				width: 200,
				sortable: true,
			},
			{
				field: "amount",
				headerName: "Amount",
				width: 150,
				sortable: true,
			},
			{
				field: "remaining",
				headerName: "Remaining",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					const remaining = calcRemaining(row);
					return remaining || "-";
				},
			},
			{
				field: "source",
				headerName: "Source",
				width: 150,
				sortable: true,
			},
			{
				field: "administrator",
				headerName: "Administrator",
				width: 150,
				sortable: true,
			},
			{
				field: "startDate",
				headerName: "Start",
				width: 150,
				sortable: true,
			},
			{
				field: "endDate",
				headerName: "End",
				width: 150,
				sortable: true,
			},
			{
				field: "status",
				headerName: "Status",
				width: 100,
				sortable: true,
				renderCell: ({ row }) => {
					return <StatusBadge status={row?.status} />;
				},
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 120,
				align: "center",
				sortable: false,
				renderCell: ({ row }) =>
					row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Funding Source"
											// onClick={() =>
											// 	history.push(
											// 		`${PATH_APPLICANTS_EDIT}${row.id}`,
											// 	)
											// }
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Funding Source"
											// onClick={() => {
											// 	setSelectedId(row.id);
											// 	setOpenDeleteDialog(true);
											// }}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["fundingSourceTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["fundingSourceTableColumnOrder"]),
				);
			}

			if (settingsMap["fundingSourceTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["fundingSourceTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["fundingSourceTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(
						settingsMap["fundingSourceTableColumnVisibility"],
					),
				);
			}

			if (settingsMap["fundingSourceTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["fundingSourceTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			// Avoid unnecessary state updates if sort model didn't change
			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"fundingSourceTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch();
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"fundingSourceTableColumnOrder",
					JSON.stringify(newColumnOrder),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => {
			return {
				field: col.field,
				width: col.width,
			};
		});
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"fundingSourceTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"fundingSourceTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.Inspections, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({ type: AMSModules.Inspections, payload: initialFilterState });
	};

	const fmtCurrency = (n) =>
		isNaN(n) || n == null ? "—" : `$${Number(n).toLocaleString()}`;

	// Summary stats
	const today = new Date();
	const active = fundingSources.filter((f) => f.status === "Active");
	const pending = fundingSources.filter((f) => f.status === "Pending");
	const totalActive = active.reduce((s, f) => s + (f.amount || 0), 0);
	const expiringSoon = active.filter((f) => {
		if (!f.endDate) return false;
		const d = Math.ceil((new Date(f.endDate) - today) / 86400000);
		return d >= 0 && d <= 60;
	});

	return (
		<>
			<div className="funding-stats">
				<div
					className={`funding-stat ${classes.cardBackground} ${classes.colorTitle}`}
				>
					<div className="funding-stat-num green">
						{fmtCurrency(totalActive)}
					</div>
					<div className="funding-stat-label">Active Funding</div>
				</div>
				<div
					className={`funding-stat ${classes.cardBackground} ${classes.colorTitle}`}
				>
					<div className="funding-stat-num">{active.length}</div>
					<div className="funding-stat-label">Active Sources</div>
				</div>
				<div
					className={`funding-stat ${classes.cardBackground} ${classes.colorTitle}`}
				>
					<div
						className={`funding-stat-num ${pending.length ? "yellow" : ""}`}
					>
						{pending.length}
					</div>
					<div className="funding-stat-label">Pending</div>
				</div>
				<div
					className={`funding-stat ${classes.cardBackground} ${classes.colorTitle}`}
				>
					<div
						className={`funding-stat-num ${expiringSoon.length ? "red" : ""}`}
					>
						{expiringSoon.length}
					</div>
					<div className="funding-stat-label">Expiring in 60d</div>
				</div>
			</div>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 85px)", // or any other dynamic value subtracting top nav height
					marginTop: theme.spacing(0.5),
				}}
			>
				<FundingSourceTableToolbar
					openAdd={() => setOpenDialog(true)}
					search={search}
					setSearch={setSearch}
					// status={status}
					// vehicles={vehicles}
					// setStatus={setStatus}
					// codes={codes}
					// employees={employees}
					// fundingSources={fundingSources}
					// serviceFilter={serviceFilter}
					// setServiceFilter={setServiceFilter}
					// assignmentFilter={assignmentFilter}
					// setAssignmentFilter={setAssignmentFilter}
					resetFilters={resetFilters}
					filters={filters}
					onFilterChange={handleOnFilterChange}
				/>

				<Box
					sx={{
						flex: 1,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<DataGridPro
						rows={fundingSources}
						columns={columns}
						initialState={{ pinnedColumns: { right: ["actions"] } }}
						// getRowId={(row) => row.GUID}
						getRowId={(row) => row.id}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={
							handleColumnVisibilityModelChange
						}
						pageSize={pageSize}
						rowsPerPageOptions={rowsPerPageOptions}
						onRowClick={(params) => {
							setOpenDetailDialog(true);
							setSelectedFundingSource(params?.row);
						}}
						pagination
						paginationMode="server"
						sortModel={sortModel}
						sortingMode="server"
						onSortModelChange={handleSortModelChange}
						rowCount={total}
						page={page}
						onPageChange={setPage}
						onPageSizeChange={setPageSize}
						loading={loading}
						disableSelectionOnClick
						onColumnOrderChange={handleColumnOrderChange}
						onColumnWidthChange={handleColumnWidthChange}
						sx={{
							...paginationStyle(theme, themeMode),
							color: theme.palette.colorFont,
							margin: 1,
						}}
					/>
				</Box>
			</Box>

			<AddEditFundingSourceDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onSave={() => undefined}
				viewOnly={false}
			/>

			<FundingDetailDialog 
				open={openDetailDialog}
				onClose={() => setOpenDetailDialog(false)}
				record={selectedFundingSource}
				onEdit={() => undefined}
				vehicles={vehicles}
				equipment={equipment}
				maintenance={maintenance}
				workOrders={workOrders}
				dispensableItems={dispensableItems}
			/>
		</>
	);
};

export default FundingSources;
